import {Session, createAnonymousSession, createSession} from '@quobis/sippojs';
import axios, {AxiosResponse} from 'axios';
import {BehaviorSubject} from 'rxjs';

import envService from './EnvService';

/**
 * received host-meta.json links interface
 */
export interface HostMeta {
	links: Array<{
		rel: string;
		href: string;
	}>;
}

/**
 * token info used in createSession
 */
export type Token = {
	issuer: string;
	access_token: string;
};

export class AuthService {
	WAC_URI;
	REALM;
	CLIENT_ID;
	auth_url: string | undefined;
	session: Session | null = null;
	private sessionSubject = new BehaviorSubject<Session | undefined>(undefined);
	public session$ = this.sessionSubject.asObservable(); // session observable to subscribe to session

	constructor() {
		this.WAC_URI = envService.WAC_URI;
		this.REALM = envService.REALM;
		this.CLIENT_ID = envService.CLIENT_ID;
	}

	/**
	 * Load the hots-meta.json with the auth_url link
	 * We maps all the links and retrieves the auth_url
	 */
	private async loadConfig(): Promise<void> {
		if (!this.WAC_URI || !this.REALM || !this.CLIENT_ID) {
			throw new Error('Wac config params not found');
		}
		const response = await axios.get<HostMeta>(`${this.WAC_URI}/.well-known/host-meta.json`);
		const config = {
			AUTH_URL: this.getAuthUrl(response.data),
		};
		/* eslint-disable no-console */
		console.debug('Running with the next configuration:', {...config});
		this.auth_url = config.AUTH_URL;
	}

	/**
	 * If we have auth_url stored we use it; if not, parses the hotmeta
	 * @param {HostMeta} hostMeta host-meta.json parsed
	 * @param {string} auth_url keycloak url
	 */
	private getAuthUrl(hostMeta: HostMeta): string {
		if (localStorage.getItem('AUTH_URL')) {
			/* eslint-disable no-console */
			console.warn('Environment AUTH_URL is being ignored. AUTH_URL set in local storage is being used instead.');
		}
		return localStorage.getItem('AUTH_URL') ?? hostMeta.links.find(link => link.rel === 'urn:quobis:auth:https')?.href ?? '';
	}

	private async initializePermissionManager(): Promise<void> {
		const permissionManager = this.session?.getPermissionManager();
		await permissionManager?.init();
		return Promise.resolve();
	}

	/**
	 * session registration:
	 * - createToken using WAC API
	 * - create session using createSession sdk method
	 * - connect to session using session.connect sdk method
	 * at the end we use next() to add the session to the session$ Observable
	 * If session.connect fails, throws an exception
	 * @param {string} user
	 * @param {string} password
	 */
	public async login(user: string, password: string): Promise <true | undefined> {
		await this.loadConfig();
		if (!this.auth_url) {
			return;
		}
		if (!this.WAC_URI || !this.REALM || !this.CLIENT_ID) {
			throw new Error('Wac config params not found');
		}
		const sessionConfig = {
			wacUri: this.WAC_URI,
		};
		const response = await this.createToken(this.WAC_URI, user, password, this.REALM, this.CLIENT_ID);
		const token: Token = {
			issuer: 'quobisId',
			access_token: response.data.access_token,
		};
		this.session = await createSession(token.issuer, token.access_token, sessionConfig);
		await this.session?.connect();
		await this.initializePermissionManager();
		this.sessionSubject.next(this.session);
		return true;
	}

	async createAnonimousSession(): Promise<boolean> {
		if (!this.WAC_URI) {
			throw new Error('Wac config params not found');
		}

		this.session = await createAnonymousSession('quobisId', {wacUri: this.WAC_URI});
		await this.session?.connect();
		await this.initializePermissionManager();
		this.sessionSubject.next(this.session);
		return true;
	}

	/**
	 * Create a temporal token to login in the service.
	 * @param {string} url Host where keycloak is sotred.
	 * @param {string} username Username with domain to login.
	 * @param {string} password User's password.
	 * @param {string} realm Keycloak realm for this app.
	 * @param {string} clientId Keycloak clientId for this app.
	 * @return {Observable<Token>} Observable containing the received token.
	 */
	private async createToken(url: string, username: string, password: string, realm: string, clientId: string): Promise<AxiosResponse<Token, any>> {
		const params = new URLSearchParams();
		params.append('client_id', clientId);
		params.append('grant_type', 'password');
		params.append('username', username);
		params.append('password', password);
		const messageContent = {
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		};
		const keycloakUrl = `${url}/auth/realms/${realm}/protocol/openid-connect/token`;
		return axios.post<Token>(keycloakUrl, params.toString(), messageContent);
	}

}
export const authService = new AuthService();
